import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, AlertTriangle, FileText, RefreshCw, Upload, X, Paperclip } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Public form-fill page. Routed at /forms/fill/:token. No login required —
 * the share token is the auth credential. Submits answers + optional
 * submitter name/email and renders a thank-you screen on success.
 *
 * Token lifecycle: rotated on creation, cleared on submission. Re-visiting
 * after submission shows a friendly "already submitted" message.
 */
export default function FormFillPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitterName, setSubmitterName] = useState('');
  const [submitterEmail, setSubmitterEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // {thank_you_text}

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/forms/fill/${token}`);
        if (cancelled) return;
        setForm(res.data.form);
        // Pre-seed answer keys so controlled inputs always have a value.
        const seed = {};
        for (const q of res.data.form.questions) {
          if (q.type === 'checkbox') seed[q.id] = [];
          else seed[q.id] = '';
        }
        setAnswers(seed);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.error || 'This link is invalid or has expired.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const setAnswer = (qid, value) => setAnswers((prev) => ({ ...prev, [qid]: value }));

  const toggleCheckbox = (qid, option) => {
    setAnswers((prev) => {
      const current = Array.isArray(prev[qid]) ? prev[qid] : [];
      return {
        ...prev,
        [qid]: current.includes(option)
          ? current.filter((o) => o !== option)
          : [...current, option],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Client-side required check; server re-validates anyway.
    for (const q of form.questions) {
      if (!q.is_required) continue;
      const v = answers[q.id];
      const empty = Array.isArray(v) ? v.length === 0 : !String(v || '').trim();
      if (empty) {
        alert(`Please answer: ${q.label}`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/forms/fill/${token}`, {
        answers,
        submitter_name: submitterName,
        submitter_email: submitterEmail,
      });
      setSubmitted({ thank_you_text: res.data.thank_you_text });
    } catch (err) {
      alert(err.response?.data?.error || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="card max-w-md w-full text-center">
          <AlertTriangle size={48} className="mx-auto mb-3 text-amber-500" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Link unavailable</h1>
          <p className="text-gray-600">{error}</p>
          <p className="text-xs text-gray-500 mt-4">
            If you believe this is a mistake, contact the business that sent you this link.
          </p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="card max-w-md w-full text-center">
          <CheckCircle size={48} className="mx-auto mb-3 text-green-500" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Submission received</h1>
          <p className="text-gray-600">
            {submitted.thank_you_text || 'Thank you. We have received your form.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        <div className="card">
          <div className="flex items-center space-x-2 mb-2">
            <FileText className="text-primary-600" size={22} />
            <h1 className="text-2xl font-bold text-gray-900">{form.name}</h1>
          </div>
          {form.description && (
            <p className="text-sm text-gray-600 mb-3">{form.description}</p>
          )}
          {form.intro_text && (
            <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4 text-sm text-blue-900 whitespace-pre-wrap">
              {form.intro_text}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Optional submitter contact */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="label">Your name</label>
                <input
                  type="text"
                  className="input"
                  value={submitterName}
                  onChange={(e) => setSubmitterName(e.target.value)}
                  placeholder="First and last name"
                />
              </div>
              <div>
                <label className="label">Your email</label>
                <input
                  type="email"
                  className="input"
                  value={submitterEmail}
                  onChange={(e) => setSubmitterEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </div>
            </div>

            {form.questions.map((q) => (
              <QuestionField
                key={q.id}
                question={q}
                value={answers[q.id]}
                onChange={(v) => setAnswer(q.id, v)}
                onToggleCheckbox={(opt) => toggleCheckbox(q.id, opt)}
                token={token}
              />
            ))}

            <button
              type="submit"
              disabled={submitting}
              className="btn btn-primary w-full"
            >
              {submitting ? 'Submitting…' : 'Submit form'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function QuestionField({ question, value, onChange, onToggleCheckbox, token }) {
  const required = question.is_required;
  const labelEl = (
    <label className="label">
      {question.label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
  );

  return (
    <div>
      {labelEl}
      {question.help_text && (
        <p className="text-xs text-gray-500 mb-1">{question.help_text}</p>
      )}

      {question.type === 'text' && (
        <input
          type="text"
          className="input"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={question.placeholder || ''}
          required={required}
        />
      )}

      {question.type === 'textarea' && (
        <textarea
          rows="4"
          className="input"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={question.placeholder || ''}
          required={required}
        />
      )}

      {question.type === 'date' && (
        <input
          type="date"
          className="input"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          required={required}
        />
      )}

      {question.type === 'select' && (
        <select
          className="input"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          required={required}
        >
          <option value="">— Select —</option>
          {(question.options || []).map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      )}

      {question.type === 'checkbox' && (
        <div className="space-y-1">
          {(question.options || []).map((o) => (
            <label key={o} className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={Array.isArray(value) && value.includes(o)}
                onChange={() => onToggleCheckbox(o)}
                className="w-4 h-4 text-primary-600 rounded"
              />
              <span className="text-sm text-gray-700">{o}</span>
            </label>
          ))}
        </div>
      )}

      {question.type === 'yes_no' && (
        <div className="flex space-x-4">
          {['Yes', 'No'].map((opt) => (
            <label key={opt} className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                name={`yn-${question.id}`}
                checked={value === opt}
                onChange={() => onChange(opt)}
                required={required}
              />
              <span className="text-sm text-gray-700">{opt}</span>
            </label>
          ))}
        </div>
      )}

      {question.type === 'signature' && (
        <SignaturePad value={value || ''} onChange={onChange} />
      )}

      {question.type === 'file' && (
        <FileUploader value={value || ''} onChange={onChange} token={token} />
      )}
    </div>
  );
}

/**
 * File-upload widget. On file-select, POSTs the file to the token-gated
 * upload endpoint and stores the server response (`{url, filename, size,
 * mime}`) as a JSON-encoded string in the answer's value. The submissions
 * browser detects this shape and renders it as a download link.
 *
 * Errors (oversize, disallowed mime, network) surface inline rather than
 * as a toast so the user sees them next to the input they just touched.
 */
function FileUploader({ value, onChange, token }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  // Value is either '' or a JSON-encoded {url,filename,size,mime} string.
  let meta = null;
  if (value) {
    try { meta = JSON.parse(value); } catch (_) { meta = null; }
  }

  const handlePick = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setError(null);
    setUploading(true);

    const form = new FormData();
    form.append('file', file);

    try {
      const res = await axios.post(
        `${API_URL}/forms/fill/${token}/upload`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      onChange(JSON.stringify(res.data));
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed. Try a different file.');
      // Clear the input so the user can retry with the same file if desired.
      if (inputRef.current) inputRef.current.value = '';
    } finally {
      setUploading(false);
    }
  };

  const handleClear = () => {
    setError(null);
    onChange('');
    if (inputRef.current) inputRef.current.value = '';
  };

  const humanSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div>
      {meta ? (
        <div className="flex items-center justify-between p-2 bg-green-50 border border-green-200 rounded">
          <div className="flex items-center space-x-2 min-w-0">
            <Paperclip size={14} className="text-green-700 shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-900 truncate">{meta.filename}</div>
              <div className="text-xs text-gray-500">{humanSize(meta.size)}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClear}
            className="p-1 text-gray-500 hover:text-red-600"
            title="Remove file"
          >
            <X size={16} />
          </button>
        </div>
      ) : (
        <label
          className={`flex items-center justify-center space-x-2 p-3 border-2 border-dashed rounded cursor-pointer transition-colors ${
            uploading ? 'border-gray-300 bg-gray-50' : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'
          }`}
        >
          <Upload size={16} className={uploading ? 'text-gray-400' : 'text-primary-600'} />
          <span className="text-sm text-gray-700">
            {uploading ? 'Uploading…' : 'Click to upload a file'}
          </span>
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            onChange={handlePick}
            disabled={uploading}
            accept="image/*,application/pdf,.doc,.docx"
          />
        </label>
      )}
      {error && (
        <p className="text-xs text-red-600 mt-1">{error}</p>
      )}
      <p className="text-xs text-gray-500 mt-1">Max 10 MB. Images, PDFs, and Word documents.</p>
    </div>
  );
}

/**
 * Inline signature pad. Renders a <canvas>, captures mouse and touch
 * input, draws smooth lines, and emits the canvas as a base64 PNG data
 * URL via onChange when the user lifts the pointer. Storage-cheap (a
 * 600x180 signature is ~10–20 KB encoded). Includes a Clear button.
 *
 * Touch events use passive:false-style preventDefault to keep the page
 * from scrolling while the user signs on mobile.
 */
function SignaturePad({ value, onChange }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });

  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Crisp lines on retina: scale the bitmap to devicePixelRatio while
    // keeping the CSS-controlled size unchanged.
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#111827';
    // If we already have a saved value (e.g. user navigated back), redraw it.
    if (value) {
      const img = new window.Image();
      img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
      img.src = value;
    }
  }, [value]);

  useEffect(() => {
    setupCanvas();
    // Redraw on resize so the bitmap stays in sync with the CSS box.
    window.addEventListener('resize', setupCanvas);
    return () => window.removeEventListener('resize', setupCanvas);
  }, [setupCanvas]);

  const pointFromEvent = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = pointFromEvent(e);
  };

  const moveDraw = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = pointFromEvent(e);
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
  };

  const endDraw = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const dataUrl = canvasRef.current.toDataURL('image/png');
    onChange(dataUrl);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.clearRect(0, 0, rect.width, rect.height);
    onChange('');
  };

  return (
    <div>
      <div className="border-2 border-gray-300 rounded-lg bg-white">
        <canvas
          ref={canvasRef}
          className="w-full block touch-none"
          style={{ height: 180 }}
          onMouseDown={startDraw}
          onMouseMove={moveDraw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={moveDraw}
          onTouchEnd={endDraw}
        />
      </div>
      <div className="flex items-center justify-between mt-1">
        <p className="text-xs text-gray-500">Sign in the box above using your mouse or finger.</p>
        <button
          type="button"
          onClick={clear}
          className="text-xs font-medium text-gray-600 hover:text-gray-900 inline-flex items-center space-x-1"
        >
          <RefreshCw size={12} />
          <span>Clear</span>
        </button>
      </div>
    </div>
  );
}
